import './Artifacts.css';

import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from 'react-oidc-context';
import { useSearchParams } from 'react-router-dom';

import { useSessionFeatureState } from '@hooks/storeHooks/useSessionState';
import { fetchArtifact, type FetchArtifactProps } from '../../../api/artifacts';
import { useArtifacts } from '../../../hooks/storeHooks/useArtifacts';
import { ArtifactButton } from './ArtifactButton';
import { ArtifactPanel } from './ArtifactPanel';

interface ArtifactsProps {
  mentorId: string;
}

interface SSEMessage {
  answer: string;
  is_chunk: boolean;
  is_complete: boolean;
  mentor_id: string;
  status: string;
}

export const Artifacts: React.FC<ArtifactsProps> = ({ mentorId }) => {
  const { t } = useTranslation();
  const { user } = useAuth();

  const [searchParams] = useSearchParams();
  const openId = searchParams.get('openId');

  const abortControllerRef = useRef<AbortController | null>(null);

  const [isSummarization, setIsSummarization] = useState(false);
  const [artifactText, setArtifactText] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [currentArtifactName, setCurrentArtifactName] = useState(t('generation.summary'));

  const {
    artifactsList,
    artifactPanelOpen,
    toggleArtifactPanel,
    setSummarization,
  } = useArtifacts();

  const { socketId } = useSessionFeatureState();

  const onResponseUpdate = (streamMessage: SSEMessage) => {
    if (streamMessage.is_complete) return;
    setIsLoading(false);
    setArtifactText((state) => state + streamMessage.answer);
  };

  const handleFetchArtifact = async (artifact_id?: string) => {
    setIsLoading(true);
    toggleArtifactPanel(true);
    try {
      abortControllerRef.current = new AbortController();

      const params: FetchArtifactProps = {
        token: user?.access_token || '',
        mentor_id: mentorId || '',
        socket_id: socketId || '',
        onResponseUpdate,
        onAbort: (controller) => {
          abortControllerRef.current = controller; // Store abort controller
        },
      };
      if (!artifact_id) {
        setIsSummarization(true);
      } else {
        params.artifact_id = artifact_id;
      }

      await fetchArtifact(params);
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') {
        console.log('SSE decoding aborted.');
      }
    }
  };

  const handleClosePanel = () => {
    abortControllerRef.current?.abort();
    setArtifactText('');
    setCurrentArtifactName(t('generation.summary'));
    toggleArtifactPanel(false);
    setIsSummarization(false);
    toggleArtifactPanel(false);
    abortControllerRef.current = null;
  };

  const onClick = (artifact_id?: string) => {
    // Find the artifact name based on the ID
    if (artifact_id) {
      const artifact = artifactsList?.find((a) => a.id === artifact_id);
      setCurrentArtifactName(artifact?.artifactName || t('generation.artifact'));
    } else {
      setCurrentArtifactName(t('generation.summary'));
    }

    handleFetchArtifact(artifact_id).catch((error) => {
      console.error('Error fetching artifact:', error);
    });
  };

  useEffect(() => {
    if (isSummarization) setSummarization(artifactText);

    return () => {
      setSummarization('');
      setIsSummarization(false);
    };
  }, [artifactText, isSummarization]);

  useEffect(
    () => () => {
      handleClosePanel();
    },
    [],
  );

  useEffect(() => {
    if (openId) {
      // Set artifact name when opening from URL
      const artifact = artifactsList?.find((a) => a.id === openId);
      setCurrentArtifactName(artifact?.artifactName || t('generation.artifact'));
      handleFetchArtifact(openId);
    }
  }, [openId, artifactsList]);

  return (
    <>
      {!artifactPanelOpen && (
        <div className="fixed bottom-10 right-8 flex items-center gap-4  w-auto z-[1001]">
          {/* NA-300 */}
          {/* {artifactsList?.map((artifact, index) => (
            <ArtifactButton
              key={index}
              icon={artifact.iconName}
              tooltip={artifact.artifactName}
              id={artifact.id}
              disabled={artifactPanelOpen}
              onClick={onClick}
            />
          ))} */}
          <ArtifactButton
            tooltip={t('generation.summary')}
            icon="pen"
            disabled={artifactPanelOpen}
            onClick={onClick}
          />
        </div>
      )}
      <ArtifactPanel
        showPanel={artifactPanelOpen}
        text={artifactText}
        isLoading={isLoading}
        handleClosePanel={handleClosePanel}
        artifactName={currentArtifactName}
      />
    </>
  );
};
